import { parseHHMMMinutes } from './hhmm';
import type { LegJson } from '../types/LegJson';

/** Seconds in a minute — the only unit conversion the leg helpers below need. */
export const SECS_PER_MIN = 60;

type WalkingLeg = Extract<LegJson, { type: 'walking' }>;

/**
 * Display label for a walking leg's duration derived from the same HH:MM
 * strings the timeline renders — `arrivalMin − departureMin`, floored to
 * whole minutes so the label always matches the visible clock. Returns
 * `"<1 min"` for walks whose start and end fall inside the same minute, and
 * falls back to `Math.floor(walk_seconds / SECS_PER_MIN)` when either
 * timestamp is malformed.
 */
export function walkLabel(walking: WalkingLeg): string {
  const departureMin = parseHHMMMinutes(walking.departure);
  const arrivalMin = parseHHMMMinutes(walking.arrival);
  if (departureMin === undefined || arrivalMin === undefined) {
    return walkLabelFromSeconds(walking.walk_seconds);
  }
  const visibleShift = Math.max(0, arrivalMin - departureMin);
  return visibleShift < 1 ? '<1 min' : `${String(visibleShift)} min`;
}

function walkLabelFromSeconds(seconds: number): string {
  const minutes = Math.floor(seconds / SECS_PER_MIN);
  return minutes < 1 ? '<1 min' : `${String(minutes)} min`;
}

/** Metres → "180 m" for sub-kilometre walks, "1.2 km" (one decimal) above 1000. */
export function formatWalkMeters(meters: number): string {
  const METRES_PER_KM = 1000;
  if (meters < METRES_PER_KM) return `${String(meters)} m`;
  const kilometres = meters / METRES_PER_KM;
  return `${kilometres.toFixed(1)} km`;
}
